import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Home, Mail, Menu, X } from 'lucide-react';
import AuthPopup from './AuthPopup';
import PaymentPopup from './PaymentPopup';
import AAdsBanner from './AAdsBanner';
import { useParams, useNavigate } from 'react-router-dom';

const MEDIA_BASE_URL = "https://media.satoshiadvance.com/images";

const AboutPage = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(15); // Default to regular subscription price
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Read the URL parameter "category" (if any) and get navigate function
  const { category } = useParams();
  const navigate = useNavigate();

  // Initialize selectedOption state: if a category exists, capitalize it; otherwise, default to "About"
  const [selectedOption, setSelectedOption] = useState(category ? capitalize(category) : 'About');

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      // Close menu if click is outside both the menu and the button
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }
  
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
  
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuOpen]);

  // Update selectedOption if the URL parameter changes
  useEffect(() => {
    if (category) {
      setSelectedOption(capitalize(category));
    }
  }, [category]);

  // Utility function: convert a URL-friendly string (e.g. "bit-flip") to "Bit Flip"
  function capitalize(str) {
    return str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  // This function updates the state and the URL when a category is clicked
  const handleOptionClick = (option) => {
    setSelectedOption(option);
    const urlOption = option.toLowerCase().replace(/\s+/g, '-');
    navigate(`/about/${urlOption}`);
    setMenuOpen(false); // Close menu after selection
  };

  const handleBlogsClick = () => {
    navigate('/info-center', { state: { selectTab: 'News & Blogs' } });
    setMenuOpen(false);
  };

  const handleButtonClick = (config) => {
    if (!config) return;
    if (config.action === 'auth') {
      setShowAuthPopup(true);
    } else if (config.action === 'payment') {
      setShowPaymentPopup(true);
      setPaymentAmount(config.amount);
    } else if (config.url) {
      window.location.href = config.url;
    }
  };

  const sections = {
    About: [
      {
        image: `${MEDIA_BASE_URL}/RetroLandscape1.png`,
        title: "About Us",
        quote: "The best way to predict the future is to create it. - Peter Drucker",
        content: "Welcome to Satoshi Advance—your home base for crypto prices, lending know-how, and retro arcade fun. Track live prices for Bitcoin, Ethereum, and more; research and compare crypto borrowing and lending platforms; read our original articles; and unwind with our growing lineup of pixel-art mini-games—Bit Flip, Bit Wheel, Bit Tanks, and Bit Dogs. Our goal is to make the ever-evolving world of crypto finance clear, useful, and genuinely fun to explore."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroLandscape2.png`,
        title: "Knowledge Is Power in Crypto Lending",
        quote: "The future depends on what you do today. - Mahatma Gandhi",
        content: "By taking charge of your financial future through crypto financing, you have the power to shape the outcomes you desire. We're here to support you on that journey with plain-English guides, a regularly updated blog covering lenders, protocols, and Bitcoin-backed loans, and curated education links. We believe informed decisions lead to better financial outcomes—so we do the research legwork and lay it out clearly."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroLandscape3.png`,
        title: "Our Mission: Bridging Borrowers and Lenders",
        quote: "",
        content: "Our mission is to seamlessly connect cryptocurrency borrowers and lenders—and to make learning about crypto enjoyable along the way. Satoshi Advance helps you make sense of rapid innovations in crypto financing and guides you toward the best options for your goals. And when it's time for a break, spin the wheel, flip the coin, or fire up the tanks: one account works across every Satoshi Advance game."
      }
    ],
    "Bit Flip": [
      {
        image: `${MEDIA_BASE_URL}/RetroBitFlip1.png`,
        title: "Bitcoin or Satoshi",
        quote: "Rockets are cool. There’s no getting around that. - Elon Musk",
        content: "Play the Bit Flip Game today! Flick up to flip the coin. This coin flipping game transforms the classic flip of a coin into a modern, interactive experience with friends. Flip a virtual coin with a Bitcoin on one side and a Satoshi symbol on the other to decide who gets to break in a game of pool or who takes the backseat in the car, or who has to take out the trash next. Players must guess mid-flip, adding suspense and excitement to the decision-making process. It's an engaging way to bring a futuristic twist to the age-old tradition of making decisions by chance."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroBitFlip2.png`,
        title: "Guess a Side, Flip a coin with Friends",
        quote: "2000 years ago one man got nailed to a tree for saying how great it would be if everyone was nice to each other for a change. - Douglas Adams",
        content: "But the Bit Flip Game isn't just about flipping a coin with friends; it's about the experience. Play heads or tails with a virtual Bitcoin while swiping down to enjoy immersive music, swiping right to skip tracks, or swiping left to change the glow color. Personalization makes the game vibrant, dynamic, and entertaining. Challenge your friends to a best-of-three or best-of-five series and enjoy this reimagined version of a timeless activity. Just Swipe up to Flip and guess which side wins now!."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroBitFlip3.png`,
        title: "Pick a side, any side",
        quote: "If people like you, they’ll listen to you, but if they trust you, they’ll do business with you. - Zig Ziglar",
        content: "Beyond just being a game, Bit Flip is a unique way to connect and build trust. Flip a coin with friends and experience laughter, friendly competition, and fun. With its simple mechanics and engaging design, the game elevates flipping a virtual coin into a meaningful and memorable activity that everyone can enjoy together. Bit Flip with your friends today!"
      }
    ],
    "Bit Wheel": [
      {
        image: `${MEDIA_BASE_URL}/RetroBitWheel1.png`,
        title: "Spin Mechanics",
        quote: "“I got a fever, and the only prescription is more cowbell” - Christopher Walken",
        content: "42 Bit Wheel is a roulette-style high-score game built around an epic theme: Bitcoin vs. Satoshi. The wheel's 42 segments split into a Satoshi blue half and a Bitcoin orange half worth 100 points each, yellow Tie (200) and Super Tie (250) zones along the equator, and green Super Win (500) and Mega Win (1,000) segments at the top and bottom—with #1 sitting dead center at the top. Every spin is resolved server-side with cryptographically fair random numbers, then the wheel animates to the exact winning segment."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroBitWheel2.png`,
        title: "Spin to Win",
        quote: "“If you really want to understand something, the best way is to try and explain it to someone else.” – Douglas Adams",
        content: "Every game is 10 spins, funded by 10 spin points drawn from your account balance. Before each spin you wager 0, 1, or 2 spin points and call a color—and, if you're feeling bold, an exact number from 1 to 42. Call it right and the multipliers stack: 3× for blue or orange, 6× for yellow, 7× for green, and a massive 42× for nailing the exact number. Land wager 2 on green #1 or #22 with the number called and you'll bank the maximum single spin: 588,000 points."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroBitWheel3.png`,
        title: "Gameplay Strategy and Technical Integration",
        quote: "“Focused, hard work is the real key to success.” – John Carmack",
        content: "The strategy is in the markers: 10 spin points across 10 spins means you can't wager 2 every time—budgeting when to press and when to observe with a 0 wager IS the game. Your score and your spin points are two separate ledgers, so every payout only ever grows your total. The retro scoreboard tracks your score, markers, spins left, and personal high score in real time, and your account dashboard keeps your lifetime stats and best runs. Train solo on the Bitcoin side today—live opponents and AI challengers are on the roadmap."
      }
    ],
    "Bit Tanks": [
      {
        image: `${MEDIA_BASE_URL}/RetroBitTanks1.png`,
        title: "Aim and Fire",
        quote: "Empty your mind; be formless, shapeless – like water.” – Bruce Lee",
        content: "In Bit Tanks, the key to victory lies in your aim. Adjust your tank's angle higher or lower to line up the perfect shot. Set the velocity to control the power of your attack and watch your shot arc across the Bitcoin-driven terrain toward your opponent."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroBitTanks2.png`,
        title: "Dynamic Terrain",
        quote: "“Every strike brings me closer to the next home run.” – Babe Ruth",
        content: "The battlefield in Bit Tanks isn't static—it moves with Bitcoin's price action. The scrolling ground mimics real-time price fluctuations, adding a strategic layer to every shot. Stay sharp and adapt your tactics to the ever-changing terrain."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroBitTanks3.png`,
        title: "Two Hits to Win",
        quote: "“What you are thinking is what you are becoming.” – Muhammad Ali",
        content: "Victory requires precision and persistence. Each tank can withstand one hit, but the second strike seals the game. Outmaneuver your opponent, land two solid hits, and claim your place as the ultimate Bit Tanks champion!"
      }
    ],
    "Bit Dogs": [
      {
        image: `${MEDIA_BASE_URL}/RetroSubscribe3.png`,
        title: "Retro Dog Breeds & 8-Bit Beginnings",
        quote: "“Dogs are better than human beings because they know but do not tell.” – Emily Dickinson",
        content: "Explore a nostalgic world of dog breeds in playful pixel-art fashion. Every pup is drawn in classic 8-bit style, from Bulldogs to Beagles to Border Collies, each with fun facts about temperament, history, and the quirks that make the breed lovable. It's part game, part retro breed encyclopedia—built for dog lovers who grew up on arcade pixels."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroSubscribe2.png`,
        title: "Chiptune Music & Wagging Tails",
        quote: "“Money can buy you a fine dog, but only love can make him wag his tail.” – Kinky Friedman",
        content: "Immerse yourself in classic chiptune melodies while you discover the charming quirks of your favorite breeds. The soundtrack channels pure 8-bit nostalgia—upbeat, loopable tunes that make browsing breed spotlights feel like leveling up in a vintage arcade. Turn the volume up and let the pixel pups set the pace."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroSubscribe1.png`,
        title: "Find the Bearded Collie Challenge",
        quote: "“Happiness is a warm puppy.” – Charles M. Schulz",
        content: "Test your spotting skills in our retro-pixel environment. A crowd of look-alike pixel pups fills the screen and somewhere in the pack hides the shaggy Bearded Collie—find it before the clock (or your friends) beat you to it. Quick eyes win bragging rights; rematches are mandatory."
      }
    ],
    Subscribe: [
      {
        image: `${MEDIA_BASE_URL}/RetroSubscribe1.png`,
        title: "Free Subscription: Lending & Borrowing Made Simple",
        quote: "“An entrepreneur is someone who has a vision for something and a want to create.” — David Karp",
        content: "Join SatoshiAdvance.com for free and take your first step into the world of decentralized lending and borrowing. With a free subscription, you can create a personalized profile and gain access to basic tools for managing and exploring cryptocurrency loans. Stay informed with curated updates and insights tailored to help you grow and manage your digital assets. All it takes is your email to sign up—no hidden costs, no obligations. Start building your crypto knowledge and connections today."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroSubscribe2.png`,
        title: "Premium Access: Unlimited Features & Mini Games",
        quote: "“I may not have gone where I intended to go, but I think I have ended up where I needed to be.” -- Douglas Adams",
        content: "Unlock the full potential of SatoshiAdvance.com with a one-time payment of $15. This premium subscription includes everything in the free tier, plus exclusive content, access to unlimited mini-games, and the ability to game with your friends in an exciting and interactive environment. Whether you're exploring new strategies in lending and borrowing or diving into fun and competitive gaming experiences, the premium tier offers unparalleled value and engagement for crypto enthusiasts of all levels."
      },
      {
        image: `${MEDIA_BASE_URL}/RetroSubscribe3.png`,
        title: "Donor Membership: Support Growth, Education, & Entertainment",
        quote: "“If you just work on stuff that you like and you’re passionate about, you don’t have to have a master plan with how things will play out.” — Mark Zuckerberg",
        content: "For those passionate about growing and giving back to the crypto community, becoming a donor offers a meaningful way to support SatoshiAdvance.com. Your contribution will help expand our platform, enhance educational resources, and create even more engaging games and tools for users worldwide. Donor memberships empower us to reach and inspire more people, fostering a deeper understanding of blockchain technology while delivering fun and accessible experiences. Join us as a valued supporter and help shape the future of crypto innovation and entertainment."
      }
    ],
    "Advertise": [
      {
        image: `${MEDIA_BASE_URL}/RetroAdvertise1.png`,
        title: "Advertise",
        quote: "“Stopping advertising to save money is like stopping your watch to save time.” – Henry Ford",
        content: "Satoshi Advance reaches a crypto-curious audience across our homepage price displays, our lending and borrowing blog, and four original mini-games—Bit Flip, Bit Wheel, Bit Tanks, and Bit Dogs. We offer banner placements, sponsored articles, and in-game promotions that put your brand in front of readers and players who live and breathe Bitcoin. Email contact@satoshiadvance.com and we'll help you find the placement that fits your goals and budget."
      }
    ]
  };

  const menuOptions = [
    'About',
    'Bit Flip',
    'Bit Wheel',
    'Bit Tanks',
    'Bit Dogs',
    'Blogs',
    'Become A Member',
    'Advertise'
  ];

  // "Become A Member" shows the membership/subscription info cards; the old
  // /about/subscribe URL keeps working via this alias.
  sections['Become A Member'] = sections.Subscribe;

  

  const handleHomeClick = () => {
    window.location.href = '/';  // Navigate to home page
  };

  const HeaderBar = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%',
        backgroundColor: '#fff',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          boxSizing: 'border-box',
          position: 'relative', // For absolute positioning of title
        }}
      >
        {/* Left side - Home icon */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={handleHomeClick}
        >
          <Home size={20} color="#0066cc" />
        </motion.div>
  
        {/* Center - Title */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              fontWeight: '600',
              fontSize: '1.1rem',
              color: '#0066cc',
            }}
          >
            Satoshi Advance
          </span>
        </div>
  
        {/* Right side - Hamburger Menu */}
        <div style={{ marginLeft: 'auto' }}>
          <motion.div
            ref={buttonRef}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '50%',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
            }}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <X size={20} strokeWidth={2} color="#0066cc" />
            ) : (
              <Menu size={20} strokeWidth={2} color="#0066cc" />
            )}
          </motion.div>
  
          {/* Dropdown Menu */}
          {menuOpen && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                width: isDesktop ? '250px' : '200px',
                backgroundColor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '0 0 8px 8px',
                overflow: 'hidden',
                zIndex: 1000,
              }}
            >
              {menuOptions.map((option) => (
                option === 'Blogs' ? (
                  <motion.div
                    key="Blogs"
                    whileHover={{ backgroundColor: '#f5f5f5' }}
                    style={{
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      color: '#333',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={handleBlogsClick}
                  >
                    Blogs
                  </motion.div>
                ) : (
                  <motion.div
                    key={option}
                    whileHover={{ backgroundColor: '#f5f5f5' }}
                    style={{
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: selectedOption === option ? '#e6f0ff' : 'white',
                      color: selectedOption === option ? '#0066cc' : '#333',
                      fontWeight: selectedOption === option ? '600' : '500',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={() => handleOptionClick(option)}
                  >
                    {option}
                  </motion.div>
                )
              ))}
  
              <motion.div
                whileHover={{ backgroundColor: '#f5f5f5' }}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => {
                  setShowPaymentPopup(true);
                  setMenuOpen(false);
                }}
              >
                Subscribe
              </motion.div>
  
              <motion.div
                whileHover={{ backgroundColor: '#f5f5f5' }}
                style={{
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
                onClick={() => {
                  setShowAuthPopup(true);
                  setMenuOpen(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0066cc"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Login / Sign Up
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );

  // New ActionButtonsSection similar to InfoCenter.js
  const ActionButtonsSection = () => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '1rem',
        margin: isDesktop ? '3rem auto 2rem' : '2rem auto',
        maxWidth: isDesktop ? '800px' : '100%',
        alignItems: 'center'
      }}
    >
      {/* Contact (mailto) */}
      <motion.a
        href="mailto:contact@satoshiadvance.com"
        whileHover={{ scale: 1.05, backgroundColor: '#005bb7' }}
        whileTap={{ scale: 0.95 }}
        style={{
          display: 'inline-block',
          backgroundColor: '#0066cc',
          color: 'white',
          padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1rem' : '0.95rem',
          fontWeight: '600',
          textDecoration: 'none',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
        }}
      >
        Contact
      </motion.a>

      {/* Login/Signup (show Auth popup) */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          backgroundColor: '#0066cc',
          color: 'white',
          padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1rem' : '0.95rem',
          fontWeight: '600',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
        }}
        onClick={() => setShowAuthPopup(true)}
      >
        Login/Signup
      </motion.button>

      {/* Subscribe (show Payment popup) */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          backgroundColor: '#0066cc',
          color: 'white',
          padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
          borderRadius: '12px',
          fontSize: isDesktop ? '1rem' : '0.95rem',
          fontWeight: '600',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
        }}
        onClick={() => setShowPaymentPopup(true)}
      >
        Subscribe
      </motion.button>
    </div>
  );

  const getButtonConfig = (title) => {
    const sectionTitle = title.toLowerCase();

    const gameUrls = {
      'bitcoin or satoshi': { url: 'https://flip.satoshiadvance.com', label: 'Play Now' },
      'guess a side, flip a coin with friends': { url: 'https://flip.satoshiadvance.com', label: 'Play Now' },
      'pick a side, any side': { url: 'https://flip.satoshiadvance.com', label: 'Play Now' },
      'spin mechanics': { url: '/bitwheel', label: 'Play Now' },
      'spin to win': { url: '/bitwheel', label: 'Play Now' },
      'gameplay strategy and technical integration': { url: '/bitwheel', label: 'Play Now' },
      'aim and fire': { url: 'https://tanks.satoshiadvance.com', label: 'Play Now' },
      'dynamic terrain': { url: 'https://tanks.satoshiadvance.com', label: 'Play Now' },
      'two hits to win': { url: 'https://tanks.satoshiadvance.com', label: 'Play Now' },
      // Subscription sections
      'free subscription: lending & borrowing made simple': { action: 'auth', label: 'Subscribe' },
      'premium access: unlimited features & mini games': { action: 'payment', label: 'Upgrade Subscription' },
      'donor membership: support growth, education, & entertainment': { action: 'payment', label: 'Donor Subscription', amount: 50 }
    };

    return gameUrls[sectionTitle] || null;
  };

  const Section = ({ image, title, quote, content, extra }) => {
    const buttonConfig = getButtonConfig(title);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          backgroundColor: 'white',
          padding: '1.25rem',
          borderRadius: '10px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
          marginBottom: '2rem',
          textAlign: 'left',
          maxWidth: '800px',
          margin: '0 auto',
          minHeight: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <img
          src={image.startsWith('http') ? image : `/images/${image}`}
          alt={title}
          referrerPolicy="no-referrer"
          loading="lazy"
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '10px',
            marginBottom: '1rem',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <h2 style={{ fontSize: '1.35rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>
          {title}
        </h2>
        <blockquote style={{ fontStyle: 'italic', marginBottom: '1rem', color: '#666', textAlign: 'center' }}>
          {quote}
        </blockquote>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.7', textAlign: 'left' }}>{content}</p>

        {buttonConfig && (
          <motion.button
            onClick={() => handleButtonClick(buttonConfig)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'inline-block',
              backgroundColor: '#0066cc',
              color: 'white',
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              marginTop: '1.5rem',
              textAlign: 'center',
              alignSelf: 'center',
              cursor: 'pointer',
              border: 'none',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
          >
            {buttonConfig.label}
          </motion.button>
        )}

        {/* Optional guide content rendered inside the same card, below the
            photo section, separated by a divider */}
        {extra}
      </motion.div>
    );
  };

  // ---------------------------------------------------------------------
  // About-tab guide blocks: a comprehensive, blog-style tour of every game
  // and feature on satoshiadvance.com, interleaved between the photos.
  // ---------------------------------------------------------------------
  const Hi = ({ c, children }) => (
    <span style={{ color: c, fontWeight: 700 }}>{children}</span>
  );

  const guideStyles = {
    // Rendered INSIDE the photo card, below its content — a divider sets the
    // guide portion apart while keeping one card, one width.
    card: {
      textAlign: 'left',
      marginTop: '1.75rem',
      paddingTop: '1.75rem',
      borderTop: '2px solid #f0f0f0',
    },
    h2: {
      fontSize: isDesktop ? '1.4rem' : '1.2rem',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '0.5rem',
      lineHeight: 1.4,
    },
    h3: {
      fontSize: isDesktop ? '1.1rem' : '1rem',
      fontWeight: 'bold',
      marginTop: '1.5rem',
      marginBottom: '0.5rem',
    },
    p: {
      fontSize: '1rem',
      lineHeight: 1.7,
      marginBottom: '0.75rem',
      color: '#333',
    },
    ul: {
      fontSize: '1rem',
      lineHeight: 1.7,
      color: '#333',
      paddingLeft: '1.4rem',
      marginTop: '0.25rem',
      marginBottom: '0.75rem',
    },
    link: { color: '#0066cc', fontWeight: 700, textDecoration: 'underline' },
    tagline: {
      textAlign: 'center',
      color: '#666',
      fontStyle: 'italic',
      marginBottom: '1.25rem',
    },
  };

  const GuideRetroVibes = () => (
    <div style={guideStyles.card}>
      <h2 style={guideStyles.h2}>👾 The Retro Gaming Aesthetic</h2>
      <p style={guideStyles.tagline}>
        Built with love for the golden age of arcades.
      </p>
      <p style={guideStyles.p}>
        Everything on Satoshi Advance is crafted in a{' '}
        <Hi c="#9932CC">retro gaming aesthetic</Hi> — chunky{' '}
        <Hi c="#F7931A">pixel art</Hi>, glowing <Hi c="#3B82F6">neon dividers</Hi>,
        blocky arcade type, and CRT-era color that turns crypto prices, lending
        research, and mini-games into one continuous arcade experience. 🕹️
      </p>
      <h3 style={guideStyles.h3}>🎶 A Streamable Retro Playlist</h3>
      <p style={guideStyles.p}>
        Powering it all is a <Hi c="#059669">specially curated, streamable retro
        playlist</Hi> — original chiptune and synth tracks made to score your
        entertainment experience from the moment you press play:
      </p>
      <ul style={guideStyles.ul}>
        <li>🎧 Hit <Hi c="#059669">play on the homepage jukebox</Hi> and let the soundtrack roll while you browse prices, lenders, and blogs</li>
        <li>🎵 Every mini-game streams its own <Hi c="#F7931A">chiptune soundtrack</Hi> — flip, spin, and fire to the beat</li>
        <li>⏭️ <Hi c="#3B82F6">Skip tracks, toggle music, and tune the volume</Hi> with taps and swipes right inside each game</li>
        <li>🐕 Even the Retro Bit Dogs cards come with <Hi c="#9932CC">8-bit dog tunes</Hi> and spoken breed descriptions</li>
      </ul>
      <p style={guideStyles.p}>
        Turn the volume up — the pixels sound as good as they look. 🔊✨
      </p>
    </div>
  );

  const GuideArcade = () => (
    <div style={guideStyles.card}>
      <h2 style={guideStyles.h2}>🕹️ The Retro Arcade — Four Original Mini-Games</h2>
      <p style={guideStyles.tagline}>
        One account. Every game. Your scores, spins, flips, and gamertag follow you everywhere.
      </p>

      <h3 style={guideStyles.h3}>
        🪙 <Hi c="#F7931A">Bit Flip</Hi> — Bitcoin or Satoshi?
      </h3>
      <p style={guideStyles.p}>
        The classic coin flip, reborn in pixels. Flip a virtual coin with{' '}
        <Hi c="#F7931A">Bitcoin ₿</Hi> on one side and <Hi c="#3B82F6">Satoshi $</Hi> on the
        other — then guess mid-air.
      </p>
      <ul style={guideStyles.ul}>
        <li>📱 <Hi c="#059669">Swipe up</Hi>, double-tap, or double-click to flip</li>
        <li>🎙️ <Hi c="#059669">Say or tap</Hi> "Bitcoin" or "Satoshi" to make your guess</li>
        <li>🏆 Challenge friends to a <Hi c="#9932CC">best-of-3 or best-of-5</Hi> series</li>
        <li>🎵 Swipe for chiptune music, custom glow colors, and dark mode</li>
      </ul>
      <p style={guideStyles.p}>
        ▶ Play it at{' '}
        <a href="https://flip.satoshiadvance.com" style={guideStyles.link}>
          flip.satoshiadvance.com
        </a>
      </p>

      <h3 style={guideStyles.h3}>
        🎡 <Hi c="#3B82F6">42 Bit Wheel</Hi> — Test Your Luck, Chase the High Score
      </h3>
      <p style={guideStyles.p}>
        A roulette-style guessing game with <Hi c="#ef4444">42 segments</Hi> of Bitcoin
        orange, Satoshi blue, tie yellow, and jackpot green. Every game is 10 spins —
        budget your wagers, call a <Hi c="#F7931A">color</Hi> (or an exact{' '}
        <Hi c="#059669">number</Hi> for a 42× payout), and go for a spot on the{' '}
        <Hi c="#9932CC">leaderboard</Hi>.
      </p>
      <ul style={guideStyles.ul}>
        <li>🎯 Wager 0, 1, or 2 spin points on every spin — strategy is the game</li>
        <li>💥 Multipliers stack up to a <Hi c="#059669">588,000-point</Hi> single spin</li>
        <li>🔒 Every spin resolves server-side with provably fair randomness</li>
        <li>🥇 High scores save to your account and the shared leaderboard</li>
      </ul>
      <p style={guideStyles.p}>
        ▶ Spin it at{' '}
        <a href="/bitwheel" style={guideStyles.link}>satoshiadvance.com/bitwheel</a>
      </p>

      <h3 style={guideStyles.h3}>
        💥 <Hi c="#059669">Bit Tanks</Hi> — Artillery on a Live Bitcoin Chart
      </h3>
      <p style={guideStyles.p}>
        A retro tank shooter where the battlefield <Hi c="#F7931A">moves with the real
        Bitcoin price</Hi>. Set your angle, dial in the power, and arc shells across
        terrain drawn from live market action — it's a fun, entertaining way to{' '}
        <Hi c="#3B82F6">watch BTC movement</Hi> and check other crypto activity while
        you battle.
      </p>
      <ul style={guideStyles.ul}>
        <li>🎯 Adjust <Hi c="#059669">angle and power</Hi> to land the perfect shot</li>
        <li>📈 Live <Hi c="#F7931A">BTC candlestick chart</Hi> and multi-coin price modes (DOGE, SOL, ZEC & more)</li>
        <li>⚔️ Two hits to win — plus multiplayer challenges with friends</li>
        <li>🏷️ Create a <Hi c="#9932CC">gamertag</Hi> that works across every game</li>
      </ul>
      <p style={guideStyles.p}>
        ▶ Battle at{' '}
        <a href="https://tanks.satoshiadvance.com" style={guideStyles.link}>
          tanks.satoshiadvance.com
        </a>
      </p>
    </div>
  );

  const GuideBitDogs = () => (
    <div style={guideStyles.card}>
      <h2 style={guideStyles.h2}>
        🐶 <Hi c="#F7931A">Retro Bit Dogs</Hi> — The Star of the Show
      </h2>
      <p style={guideStyles.tagline}>
        Our main feature: a pixel-art dog universe with its own mini-game and merch store.
      </p>
      <p style={guideStyles.p}>
        <Hi c="#3B82F6">Discover new dog breeds</Hi> as one-of-a-kind retro trading
        cards — every generation is fresh 8-bit art with a real description of the
        breed's history and personality. Then put your eyes to the test:
      </p>
      <ul style={guideStyles.ul}>
        <li>🔎 <Hi c="#059669">Search a vast game board</Hi> — your dog hides somewhere on a huge pixel landscape; find it before your 3 guesses (or 60 seconds) run out</li>
        <li>💾 <Hi c="#3B82F6">Save your favorite dog cards</Hi> to your account collection</li>
        <li>✏️ <Hi c="#9932CC">Name your dog!</Hi> Give your favorite pup its own custom name</li>
        <li>🔊 Hear breed descriptions read aloud, backed by chiptune tunes</li>
      </ul>
      <h3 style={guideStyles.h3}>🧢 The Merch Store — Wear Your Favorite Breed</h3>
      <p style={guideStyles.p}>
        Turn any card in your collection into real-world gear: print{' '}
        <Hi c="#F7931A">hats</Hi>, <Hi c="#3B82F6">t-shirts</Hi>, and{' '}
        <Hi c="#059669">socks</Hi> featuring your retro dog cards — with your dog's
        custom name on them. It's the <Hi c="#ef4444">perfect gift</Hi> for the dog
        lover (or pixel-art fan) in your life. 🎁
      </p>
      <p style={guideStyles.p}>
        ▶ Find Retro Bit Dogs on the{' '}
        <a href="/" style={guideStyles.link}>homepage</a> (toggle Game Mode) and the
        merch store at{' '}
        <a href="/shop" style={guideStyles.link}>satoshiadvance.com/shop</a>
      </p>
    </div>
  );

  const GuideNewsAndTools = () => (
    <div style={guideStyles.card}>
      <h2 style={guideStyles.h2}>📰 News, Lending & Everyday BTC Tools</h2>
      <p style={guideStyles.tagline}>
        The practical side of Satoshi Advance — research, connections, and utilities.
      </p>

      <h3 style={guideStyles.h3}>📚 <Hi c="#3B82F6">Crypto News & Blog</Hi></h3>
      <p style={guideStyles.p}>
        Read original articles on <Hi c="#F7931A">Bitcoin-backed loans</Hi>, lending
        platforms, market trends, and crypto updates in our{' '}
        <a href="/info-center" style={guideStyles.link}>Info Center</a> — plain-English
        research so you can make informed decisions.
      </p>

      <h3 style={guideStyles.h3}>🤝 <Hi c="#059669">Find Lenders & Borrowers</Hi></h3>
      <ul style={guideStyles.ul}>
        <li>🏦 Browse <Hi c="#F7931A">2025's Top Crypto Lenders</Hi>, ranked and reviewed on the homepage</li>
        <li>📋 Apply as a <Hi c="#3B82F6">borrower</Hi> or list yourself as a <Hi c="#059669">lender</Hi> through the Borrow / Lend section</li>
        <li>🎓 Curated <Hi c="#9932CC">education links</Hi> to trusted crypto learning resources</li>
      </ul>

      <h3 style={guideStyles.h3}>🛠️ <Hi c="#F7931A">Useful BTC Tools</Hi></h3>
      <ul style={guideStyles.ul}>
        <li>🛒 <Hi c="#059669">Shop online with crypto</Hi> — the Crypto Purchase Finder shows which major retailers take Bitcoin, and how</li>
        <li>💱 <Hi c="#3B82F6">Live exchange rates</Hi> — current prices for BTC, ETH, DOGE and more, right on the homepage</li>
        <li>🔢 <Hi c="#9932CC">USD → BTC/Sats converter</Hi> — see any dollar amount in Bitcoin and satoshis instantly</li>
        <li>📊 Fun comparisons — like what $100 (or a $50,000 sports car 🏎️) looks like in sats</li>
      </ul>
      <p style={guideStyles.p}>
        Everything lives at{' '}
        <a href="/" style={guideStyles.link}>satoshiadvance.com</a> — and one free
        account unlocks it all. 🚀
      </p>
    </div>
  );

  // First card leads with the retro aesthetic & playlist section, then the
  // arcade guide — all inside the same card.
  const aboutGuideBlocks = [
    <><GuideRetroVibes /><GuideArcade /></>,
    <GuideBitDogs />,
    <GuideNewsAndTools />
  ];

  // Rendered inside the first Become A Member card: every way to enjoy and
  // support satoshiadvance.com.
  const GuideMembership = () => (
    <div style={guideStyles.card}>
      <h2 style={guideStyles.h2}>🎉 All the Ways to Enjoy Satoshi Advance</h2>
      <p style={guideStyles.tagline}>
        Free to join — and plenty of ways to level up or support the mission.
      </p>

      <h3 style={guideStyles.h3}>💜 <Hi c="#9932CC">Three Donation Levels</Hi></h3>
      <p style={guideStyles.p}>
        Love what we're building? Choose from <Hi c="#9932CC">three donation
        levels</Hi> to support the creators directly — every contribution funds
        more great features, fresh pixel art, and brand-new mini games for the
        whole community.
      </p>

      <h3 style={guideStyles.h3}>🕹️ <Hi c="#3B82F6">Buy Game Packs</Hi></h3>
      <ul style={guideStyles.ul}>
        <li>🎡 Packs of <Hi c="#3B82F6">spins</Hi> for the 42 Bit Wheel — chase that high score</li>
        <li>🪙 Packs of <Hi c="#F7931A">flips</Hi> for Bit Flip showdowns with friends</li>
        <li>💥 Packs of <Hi c="#059669">tank battles</Hi> for Bit Tanks supremacy</li>
      </ul>

      <h3 style={guideStyles.h3}>🐶 <Hi c="#F7931A">Dog Cards & Retro Merch</Hi></h3>
      <ul style={guideStyles.ul}>
        <li>🃏 Collect and save <Hi c="#3B82F6">retro Dog Cards</Hi> — discover new breeds and name your pup</li>
        <li>🧢 Print <Hi c="#F7931A">Dog Merch</Hi>: hats, t-shirts, and socks featuring your favorite cards</li>
        <li>🛍️ Browse other <Hi c="#9932CC">retro Satoshi Advance merchandise</Hi> in the <a href="/shop" style={guideStyles.link}>shop</a></li>
      </ul>

      <p style={guideStyles.p}>
        However you play — free spins, game packs, dog cards, or donations —
        one account works across every game and the homepage. 🚀
      </p>
    </div>
  );

  const memberGuideBlocks = [<GuideMembership />, null, null];

  const ContactSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{
        backgroundColor: 'white',
        padding: '1rem',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        marginTop: '2rem',
        textAlign: 'center',
        maxWidth: '800px',
        margin: '2rem auto',
      }}
    >
      <motion.a
        href="mailto:contact@satoshiadvance.com"
        whileHover={{ scale: 1.05, backgroundColor: '#005bb7' }}
        whileTap={{ scale: 0.95 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          maxWidth: '300px',
          margin: '0 auto',
          backgroundColor: '#0066cc',
          color: 'white',
          padding: '1rem',
          borderRadius: '12px',
          fontSize: '1rem',
          fontWeight: '600',
          textAlign: 'center',
          textDecoration: 'none',
          boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)',
          letterSpacing: '0.5px',
          transition: 'all 0.3s ease',
        }}
      >
        <Mail size={20} />
        <span>Get in Touch!</span>
      </motion.a>
    </motion.div>
  );
  
  

  return (
    <div
      id="top"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: isDesktop ? '1.5rem' : '0.75rem',
        overflowY: 'auto',
      }}
    >
      <HeaderBar />
      
      {/* Spacer for fixed header */}
      <div style={{ height: isDesktop ? '80px' : '60px' }}></div>

      {/* Ad banner above the content on every About tab */}
      <AAdsBanner />

      {(sections[selectedOption] || sections.About).map((section, index) => (
        <React.Fragment key={index}>
          {/* A banner directly above each card after the first (the first
              already has the page-top banner above it) — 4 ads total */}
          {index > 0 && <AAdsBanner />}
          <Section
            {...section}
            // Some tabs carry a guide section inside the card (same card,
            // same width) below the photo content.
            extra={
              selectedOption === 'About'
                ? aboutGuideBlocks[index]
                : (selectedOption === 'Become A Member' || selectedOption === 'Subscribe')
                  ? memberGuideBlocks[index]
                  : null
            }
          />
        </React.Fragment>
      ))}

      


      {/* Action Buttons Section */}
      <ActionButtonsSection />

      {/* Ad banner at the bottom of every About tab */}
      <AAdsBanner />

      {/* Back to Top link */}
      <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
        <a
          href="#top"
          style={{
            textDecoration: 'none',
            color: '#0066cc',
            fontWeight: 'bold',
            fontSize: isDesktop ? '1rem' : '0.95rem'
          }}
        >
          Back to Top
        </a>
      </div>

      {showAuthPopup && <AuthPopup onClose={() => setShowAuthPopup(false)} />}
      {showPaymentPopup && (
        <PaymentPopup
          onClose={() => setShowPaymentPopup(false)}
          amount={paymentAmount}
        />
      )}
    </div>
  );
};

export default AboutPage;

